import { assign, createActor, setup } from "xstate";

/**
 * Structural line kinds for carousel suggestion classification.
 * Sensors are intentionally small; the state machine decides drop-in vs chat-only.
 */
export type SuggestionLineKind =
  | "blank"
  | "assessment_marker"
  | "edit_directive"
  | "consent_cta"
  | "market_meta"
  | "quoted_speech"
  | "prose";

export type DropInClassification = "drop_in" | "non_drop_in";

type ClassifierContext = {
  imperativeCount: number;
  proseChars: number;
  quotedSpeechLines: number;
  assessmentSeen: boolean;
  consentSeen: boolean;
  marketMetaSeen: boolean;
};

type ClassifierEvent =
  | { type: "LINE"; kind: SuggestionLineKind; charCount: number }
  | { type: "END" };

const ASSESSMENT_MARKERS = [
  "final assessment",
  "overall assessment",
  "market assessment",
  "editorial assessment",
] as const;

const CONSENT_MARKERS = [
  "switch the composer to allow edits",
  "switch to allow edits",
  "to apply this change in your manuscript",
  "want me to insert",
  "want me to replace",
] as const;

const MARKET_META_MARKERS = [
  "market-ready",
  "market ready",
  "dark romance parameters",
  "genre obligations",
  "market expectations",
] as const;

/**
 * Verbs that almost always introduce craft/edit-audit labels, not manuscript action.
 * Ambiguous story verbs (cut/add/tighten/open/…) are left as prose; existing advice
 * regexes in writeCarouselSuggestionSanitize still catch "Cut the hedging" craft lines.
 */
const EDIT_DIRECTIVE_STARTS = [
  "sharpen",
  "audit",
  "eliminate",
  "consider",
  "avoid",
  "deepen",
] as const;

function normalizeLine(line: string): string {
  return String(line ?? "")
    .replace(/^\s*#{1,6}\s*/, "")
    .replace(/^\s*[-*]\s+/, "")
    .replace(/\*\*/g, "")
    .trim();
}

function startsWithAny(haystack: string, prefixes: readonly string[]): boolean {
  const lower = haystack.toLowerCase();
  return prefixes.some((prefix) => lower === prefix || lower.startsWith(`${prefix} `) || lower.startsWith(`${prefix}:`));
}

function includesAny(haystack: string, needles: readonly string[]): boolean {
  const lower = haystack.toLowerCase();
  return needles.some((needle) => lower.includes(needle));
}

/**
 * Critique chrome markers should not fire on incidental phrases inside narrative prose
 * (e.g. "market-ready" embedded in a she/he action sentence).
 */
function includesChromeMarker(haystack: string, needles: readonly string[]): boolean {
  const normalized = normalizeLine(haystack);
  if (!normalized || !includesAny(normalized, needles)) return false;
  const looksNarrative =
    /\b(?:she|he|they|I|we)\s+[a-z]+\b/i.test(normalized)
    || /["“”‘’]/.test(normalized);
  // Incidental marker use inside narrative stays prose; short meta/CTA lines still match.
  if (looksNarrative && normalized.length >= 80) return false;
  return true;
}

/** Map one physical/logical line into a structural event for the classifier machine. */
export function classifySuggestionLine(rawLine: string): SuggestionLineKind {
  const line = normalizeLine(rawLine);
  if (!line) return "blank";
  if (includesChromeMarker(line, ASSESSMENT_MARKERS)) return "assessment_marker";
  if (includesChromeMarker(line, CONSENT_MARKERS)) return "consent_cta";
  if (includesChromeMarker(line, MARKET_META_MARKERS)) return "market_meta";
  if (/^["“‘'].+|["”’']$/.test(line) || (line.includes('"') && line.length < 220)) {
    return "quoted_speech";
  }
  // Narrative imperatives that are drop-in prose, not craft directives.
  if (/^let\s+(?:her|him|them|me|us)\b/i.test(line)) return "prose";
  if (/^make\s+(?:her|him|them|the|a|an)\b/i.test(line)) return "prose";
  if (/^(?:open|close|start|end)\s+(?:the|a|an|her|his|their)\b/i.test(line)) return "prose";
  // Short craft/edit-audit labels only (e.g. "Sharpen the ending's edge").
  // Do not treat manuscript action verbs (cut/add/tighten/…) as directives.
  if (
    line.length < 96
    && startsWithAny(line, EDIT_DIRECTIVE_STARTS)
    && !/\b(she|he|they|I|we|her|him|them)\b/i.test(line)
  ) {
    return "edit_directive";
  }
  return "prose";
}

/** Split suggestion text into classifier line events (blank lines omitted). */
export function tokenizeSuggestionLines(suggestion: string): Array<{ kind: SuggestionLineKind; charCount: number }> {
  const lines = String(suggestion ?? "")
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);
  return lines.map((line) => {
    const kind = classifySuggestionLine(line);
    return { kind, charCount: line.length };
  });
}

const dropInClassifierMachine = setup({
  types: {} as {
    context: ClassifierContext;
    events: ClassifierEvent;
  },
  guards: {
    lineIsAssessment: ({ event }) => event.type === "LINE" && event.kind === "assessment_marker",
    lineIsConsent: ({ event }) => event.type === "LINE" && event.kind === "consent_cta",
    lineIsMarketMeta: ({ event }) => event.type === "LINE" && event.kind === "market_meta",
    /**
     * LTL-style end check:
     * F(assessment ∨ consent ∨ market_meta) ∨ (edit_directive≥2 ∧ proseChars small) → non_drop_in
     * else prose/quoted bulk → drop_in
     */
    endIsNonDropIn: ({ context }) => {
      if (context.assessmentSeen || context.consentSeen || context.marketMetaSeen) return true;
      if (context.imperativeCount >= 2 && context.proseChars < 160) return true;
      if (context.imperativeCount >= 1 && context.proseChars < 40 && context.quotedSpeechLines === 0) {
        return true;
      }
      // Empty / whitespace-only after tokenization → not applyable prose.
      return context.proseChars === 0 && context.quotedSpeechLines === 0;
    },
  },
  actions: {
    accumulateLine: assign(({ context, event }) => {
      if (event.type !== "LINE") return context;
      return {
        imperativeCount:
          event.kind === "edit_directive" ? context.imperativeCount + 1 : context.imperativeCount,
        proseChars: event.kind === "prose" ? context.proseChars + event.charCount : context.proseChars,
        quotedSpeechLines:
          event.kind === "quoted_speech" ? context.quotedSpeechLines + 1 : context.quotedSpeechLines,
        assessmentSeen: context.assessmentSeen || event.kind === "assessment_marker",
        consentSeen: context.consentSeen || event.kind === "consent_cta",
        marketMetaSeen: context.marketMetaSeen || event.kind === "market_meta",
      };
    }),
    markAssessment: assign({ assessmentSeen: true }),
    markConsent: assign({ consentSeen: true }),
    markMarketMeta: assign({ marketMetaSeen: true }),
  },
}).createMachine({
  id: "carouselDropInClassifier",
  initial: "scanning",
  context: {
    imperativeCount: 0,
    proseChars: 0,
    quotedSpeechLines: 0,
    assessmentSeen: false,
    consentSeen: false,
    marketMetaSeen: false,
  },
  states: {
    scanning: {
      on: {
        LINE: [
          { guard: "lineIsAssessment", target: "nonDropIn", actions: "markAssessment" },
          { guard: "lineIsConsent", target: "nonDropIn", actions: "markConsent" },
          { guard: "lineIsMarketMeta", target: "nonDropIn", actions: "markMarketMeta" },
          { actions: "accumulateLine" },
        ],
        END: [
          { guard: "endIsNonDropIn", target: "nonDropIn" },
          { target: "dropIn" },
        ],
      },
    },
    dropIn: { type: "final" },
    nonDropIn: { type: "final" },
  },
});

/** Classify one suggestion body as drop-in manuscript prose vs chat-only notes. */
export function classifyCarouselSuggestion(suggestion: string): DropInClassification {
  const actor = createActor(dropInClassifierMachine);
  actor.start();
  for (const token of tokenizeSuggestionLines(suggestion)) {
    actor.send({ type: "LINE", kind: token.kind, charCount: token.charCount });
    if (actor.getSnapshot().value === "nonDropIn") {
      actor.stop();
      return "non_drop_in";
    }
  }
  actor.send({ type: "END" });
  const value = actor.getSnapshot().value;
  actor.stop();
  return value === "dropIn" ? "drop_in" : "non_drop_in";
}

/** True when suggestion text contains critique/assessment chrome that must never be a Write Card. */
export function suggestionHasCritiqueCarouselMarkers(suggestion: string): boolean {
  return tokenizeSuggestionLines(suggestion).some(
    (token) =>
      token.kind === "assessment_marker"
      || token.kind === "consent_cta"
      || token.kind === "market_meta",
  );
}

/** True when the body is mostly short imperative edit-audit lines, not replacement prose. */
export function suggestionLooksLikeEditDirectiveCluster(suggestion: string): boolean {
  const tokens = tokenizeSuggestionLines(suggestion);
  if (tokens.length === 0) return false;
  const directives = tokens.filter((token) => token.kind === "edit_directive").length;
  const proseChars = tokens
    .filter((token) => token.kind === "prose" || token.kind === "quoted_speech")
    .reduce((sum, token) => sum + token.charCount, 0);
  return directives >= 2 && proseChars < 160;
}

export function suggestionLooksLikeDropInRewrite(suggestion: string): boolean {
  return classifyCarouselSuggestion(suggestion) === "drop_in";
}

/**
 * LTL-style batch gate over suggestion classifications:
 * G(suggestion → drop_in) must hold for carousel chrome.
 * Returns only drop-in bodies (order preserved).
 */
export function filterDropInSuggestionsWithClassifier(
  suggestions: string[],
): string[] {
  return suggestions.filter((entry) => suggestionLooksLikeDropInRewrite(entry));
}
